// Move "Exit Ticket" / "Printable" / "Student Handout" / "Worksheet" /
// "Answer Key" sections of a lesson markdown to the END of the document.
// Exit-ticket sections are placed BEFORE other handouts so the printed
// stack reads: lesson plan → exit ticket → worksheets → answer keys.
//
// We operate on H1/H2 headings only — H3+ "Reflection and Exit Ticket"
// sub-sections inside a teacher Part stay put.
//
// The function is intentionally string-based (no Markdown parser) so it
// matches the backend's exports.py logic exactly. Both code paths use
// the same heading-prefix rules.

// Headings we consider printable handouts. Each entry MUST match the
// start of the heading text (after the `#` chars have been stripped).
const HANDOUT_PREFIXES = [
  "student handout",
  "worksheet",
  "worksheet —",
  "answer key",
  "printable",
];

const EXIT_TICKET_RE = /^\s*(?:printable[\s:—-]+)?(?:exit\s+ticket|exit\s+slip|exit\s+skill\s+ticket)\b/i;

const isExitTicketHeading = (text) => EXIT_TICKET_RE.test(text || "");

const isHandoutHeading = (text) => {
  const head = (text || "").trim().toLowerCase();
  if (isExitTicketHeading(text)) return true;
  return HANDOUT_PREFIXES.some((p) => head.startsWith(p));
};

/**
 * Returns the same markdown string with handout sections (exit tickets,
 * worksheets, student handouts, answer keys, "Printable: ..." blocks)
 * hoisted to the bottom. Exit tickets always print first among handouts.
 *
 * If the input has no recognisable handout headings, the input is
 * returned unchanged.
 */
export const reorderLessonMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== "string") return markdown;
  const lines = markdown.split("\n");

  // Split lines into top-level (H1/H2) sections. The first chunk before
  // the first H1/H2 is kept as the "preamble" so we don't lose anything
  // above the first heading.
  /** @type {{heading: string|null, headingLine: string|null, lines: string[]}[]} */
  const sections = [];
  let current = { heading: null, headingLine: null, lines: [] };

  for (const line of lines) {
    const m = /^(#{1,2})\s+(.*)$/.exec(line);
    if (m) {
      // New top-level section starts.
      if (current.heading !== null || current.lines.length > 0) {
        sections.push(current);
      }
      current = { heading: m[2].trim(), headingLine: line, lines: [] };
    } else {
      current.lines.push(line);
    }
  }
  if (current.heading !== null || current.lines.length > 0) {
    sections.push(current);
  }

  const teacher = [];
  const exitTickets = [];
  const otherHandouts = [];

  for (const sec of sections) {
    if (sec.heading !== null && isHandoutHeading(sec.heading)) {
      const bucket = isExitTicketHeading(sec.heading) ? exitTickets : otherHandouts;
      if (sec.headingLine) bucket.push(sec.headingLine);
      bucket.push(...sec.lines);
    } else {
      if (sec.headingLine) teacher.push(sec.headingLine);
      teacher.push(...sec.lines);
    }
  }

  if (exitTickets.length === 0 && otherHandouts.length === 0) return markdown;
  return [...teacher, ...exitTickets, ...otherHandouts].join("\n");
};
